import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["host", "menu", "readOnly", "defaultOptions", "draftStatePages", "options", "pagesRequestCompleted", "required", "type", "value"];
import { Component, Fragment } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import { debounce, isEqual } from 'underscore';
import Routes from 'ContentUtils/Routes';
import * as MenusActions from 'ContentData/actions/Menus';
import * as menusSelectors from 'ContentData/selectors/menus';
import { fetchPages as fetchPagesAction } from 'ContentData/actions/Pages';
import withCreateResourceModal from '../decorators/withCreateResourceModal';
import { MENU } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { DEFAULT_PAGE_SIZE, ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/cmv2/constants';
import { CONFLICT } from 'ContentUtils/enums/HttpStatusCodes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import UITag from 'UIComponents/tag/UITag';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DecoratedAsyncSelector = withCreateResourceModal(MENU)(AsyncSelector);
const i18nKey = key => `ContentComponents.cmv2.fields.menuSelect.${key}`;
const mapStateToProps = (state, props) => {
  return {
    menu: menusSelectors.getMenu(state, {
      id: props.value
    }),
    options: menusSelectors.getMenuOptions(state),
    optionsRequestStatus: menusSelectors.getMenusCacheRequestStatus(state),
    defaultOptions: menusSelectors.getDefaultMenuOptions(state),
    draftStatePages: menusSelectors.getDraftPages(state, {
      id: props.value
    }),
    allMenuPages: menusSelectors.getAllPages(state, {
      id: props.value
    }),
    pagesRequestCompleted: menusSelectors.getPagesRequestCompleted(state)
  };
};
const mapDispatchToProps = {
  fetchMenu: MenusActions.fetchMenu,
  fetchMenus: MenusActions.fetchMenus,
  saveCache: MenusActions.saveCache,
  clearCache: MenusActions.clearCache,
  createResource: MenusActions.createMenu,
  fetchPages: fetchPagesAction
};
const connector = connect(mapStateToProps, mapDispatchToProps);
class MenuSelect extends Component {
  constructor(props) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.onCreateMenu = this.onCreateMenu.bind(this);
    this.onOpenMenu = this.onOpenMenu.bind(this);
    this.renderSelectValue = this.renderSelectValue.bind(this);
    this.debouncedLoadOptions = debounce(this.loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this.state = {
      input: ''
    };
  }
  componentDidMount() {
    if (this.props.value) {
      this.props.fetchMenu(this.props.value).catch(err => {
        throw err;
      });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      fetchMenu,
      menu,
      value,
      fetchPages,
      allMenuPages
    } = this.props;
    const {
      value: prevValue,
      menu: prevMenu
    } = prevProps;
    if (!isEqual(prevValue, value) && !menu) {
      fetchMenu(value).catch(err => {
        throw err;
      });
    }
    if (!isEqual(menu, prevMenu) && menu && allMenuPages) {
      const pageIds = allMenuPages.filter(node => node.page_id).map(node => node.page_id);
      fetchPages({
        limit: pageIds.length,
        id: pageIds.join(',')
      }).catch(err => {
        throw err;
      });
    }
  }
  trackEvent(eventKey) {
    const {
      trackFieldAction
    } = this.props;
    if (trackFieldAction) {
      trackFieldAction(eventKey);
    }
  }
  onChange(evt) {
    const {
      onFieldChange = emptyFunction
    } = this.props;
    onFieldChange(evt.target.value);
    this.trackEvent('menu-select-changed');
  }
  onOpenMenu() {
    const {
      value,
      host = ''
    } = this.props;
    const menuUrl = Routes.advancedMenuSettings(value);
    window.open(`${host}${menuUrl}?source=${Routes.getCurrentApp()}`);
    this.trackEvent('menu-edit-clicked');
  }
  onCreateMenu(menuName, {
    onConflictError,
    onCreateModalClose
  }) {
    this.props.createResource(menuName).then(result => {
      if (result && result.status === CONFLICT) {
        onConflictError();
        return;
      }
      const newMenuId = result && result.id;
      if (newMenuId) {
        this.props.saveCache(newMenuId);
        this.onChange({
          target: {
            value: newMenuId
          }
        });
        this.props.fetchMenu(newMenuId).catch(err => {
          throw err;
        });
        this.onOpenMenu();
        onCreateModalClose();
        this.trackEvent('menu-create-clicked');
      }
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      this.props.clearCache();
      offset = 0;
    }
    const query = {
      order: 'name',
      name__icontains: input,
      limit: DEFAULT_PAGE_SIZE,
      offset
    };
    this.props.fetchMenus(query).then(response => {
      const {
        options = []
      } = this.props;
      const {
        total
      } = response;
      callback(null, {
        options,
        pagination: {
          hasMore: total > options.length,
          offset: offset + DEFAULT_PAGE_SIZE,
          length: total
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  renderSelectValue(opt) {
    const {
      draftStatePages
    } = this.props;
    const text = opt.text || opt.value || opt.buttonText;
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      children: [text, !!draftStatePages.length && /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx(UITag, {
          className: "m-left-2",
          use: "warning",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('containsDraft')
          })
        })
      })]
    });
  }
  render() {
    const _this$props = this.props,
      {
        host: __host = '',
        menu,
        readOnly,
        defaultOptions = [],
        draftStatePages,
        options = [],
        pagesRequestCompleted,
        required,
        type = MENU,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    let selectValue = value;

    // this populates the select with the menu ID if the value is a menu name
    if (menu) selectValue = menu.id;
    // if the value is a menu name and there's no menu with that name, display an empty selector
    else if (typeof value === 'string') selectValue = '';
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [!!draftStatePages.length && pagesRequestCompleted && /*#__PURE__*/_jsxs(UIAlert, {
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('alertHeader')
        }),
        className: "m-bottom-2 p-bottom-4",
        type: "warning",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('alertBody')
        }), /*#__PURE__*/_jsx(UIList, {
          styled: true,
          className: "p-2",
          children: draftStatePages.map(page => {
            const {
              content_type,
              label,
              page_id,
              page_title
            } = page;
            return /*#__PURE__*/_jsx(UILink, {
              href: Routes.menuDraftPageLink(content_type, page_id),
              children: label || page_title
            }, `${page_id}-${label}`);
          })
        })]
      }), /*#__PURE__*/_jsx(DecoratedAsyncSelector, Object.assign({}, rest, {
        clearable: !required,
        defaultOptions: defaultOptions,
        external: true,
        loadOptions: this.debouncedLoadOptions,
        onChange: this.onChange,
        onCreateResource: this.onCreateMenu,
        onEditResource: this.onOpenMenu,
        options: options,
        readOnly: readOnly,
        type: type,
        value: selectValue,
        valueRenderer: this.renderSelectValue
      }))]
    });
  }
}
export default connector(MenuSelect);